import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useState, type ReactNode, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import { Field, inputClass } from '../../components/Field';
import { FormProblem } from '../../components/FormProblem';
import { useToast } from '../../components/Toast';
import { useAuth } from '../auth/AuthProvider';
import {
  createDevice,
  byClient,
  deleteDevice,
  deviceKey,
  devicesKey,
  getDevice,
  identifying,
  isChannel,
  isRecorder,
  kindLabels,
  sitesKey,
  updateDevice,
  type Connection,
  type Device,
  type DeviceBody,
  type DeviceKind,
  type Site,
} from './api';
import { RecordingSection } from '../recording/RecordingSection';
import { ChannelsPanel } from './ChannelsPanel';
import { DeviceConnection } from './DeviceConnection';
import { DevicePasswordInput, SavedPassword } from './DevicePassword';
import { generateDevicePassword } from './password';
import { PushKeyPanel } from './PushKeyPanel';
import { noProblem, toProblem, type Problem } from './problem';
import {
  CHANNEL,
  detectPreset,
  detectTemplatePreset,
  presetPaths,
  presetTemplates,
  renderTemplate,
  rtspPresetLabels,
  type RtspPreset,
} from './rtspPresets';

interface Props {
  device?: Device;
  sites: Site[];
  canManage: boolean;
  onClose: () => void;
  /** Switches the drawer to another device (a channel, or a channel's recorder). */
  onOpen: (d: Device) => void;
}

const channelPresets = [4, 8, 16, 32];
const recorderKinds: DeviceKind[] = ['dvr', 'nvr'];

type PasswordMode = 'keep' | 'set' | 'remove';

interface FormState {
  name: string;
  kind: DeviceKind;
  site_id: string;
  vendor: string;
  model: string;
  host: string;
  http_port: string;
  /** '' means ONVIF answers on the web port. */
  onvif_port: string;
  rtsp_port: string;
  /** A DVR/NVR whose channels are created from path templates (ADR-018). */
  recorder: boolean;
  channel_count: string;
  connection: Connection;
  rtsp_main_path: string;
  rtsp_sub_path: string;
  /** UI-only: the URL template and channel that fill the paths. */
  preset: RtspPreset;
  channel: string;
  username: string;
  password: string;
  enabled: boolean;
  notes: string;
}

function initial(d: Device | undefined, sites: Site[]): FormState {
  const recorder = d ? isRecorder(d) : false;
  // Devices without stored paths (new, or ONVIF) start from the default template.
  const paths = d?.rtsp_main_path
    ? { main: d.rtsp_main_path, sub: d.rtsp_sub_path }
    : (presetPaths('hikvision', 1) ?? { main: '', sub: '' });
  const detected = recorder
    ? { preset: detectTemplatePreset(paths.main, paths.sub), channel: 1 }
    : detectPreset(paths.main, paths.sub);
  return {
    recorder,
    channel_count: String(d?.channel_count ?? 4),
    onvif_port: d?.onvif_port ? String(d.onvif_port) : '',
    name: d?.name ?? '',
    kind: d?.kind ?? 'camera',
    site_id: d?.site.id ?? (sites.length === 1 ? (sites[0]?.id ?? '') : ''),
    vendor: d?.vendor ?? '',
    model: d?.model ?? '',
    host: d?.host ?? '',
    http_port: String(d?.http_port ?? 80),
    rtsp_port: String(d?.rtsp_port ?? 554),
    // New devices default to direct RTSP: it needs only the RTSP port, which
    // is what installers usually forward.
    connection: d?.connection ?? 'rtsp',
    rtsp_main_path: paths.main,
    rtsp_sub_path: paths.sub,
    preset: detected.preset,
    channel: String(detected.channel),
    username: d?.username ?? '',
    // A new device gets a random password for the installer to configure on it.
    password: d ? '' : generateDevicePassword(),
    enabled: d?.enabled ?? true,
    notes: d?.notes ?? '',
  };
}

/** Builds a create body, or a patch with only the fields that changed. */
function toBody(f: FormState, d: Device | undefined, mode: PasswordMode): DeviceBody {
  // A channel only has its own name, enabled flag and notes.
  if (d && isChannel(d)) {
    return diff({ name: f.name, enabled: f.enabled, notes: f.notes }, d);
  }
  const full: DeviceBody = {
    site_id: f.site_id,
    name: f.name,
    kind: f.kind,
    vendor: f.vendor,
    model: f.model,
    host: f.host,
    http_port: Number(f.http_port),
    rtsp_port: Number(f.rtsp_port),
    connection: f.connection,
    username: f.username,
    enabled: f.enabled,
    notes: f.notes,
  };
  // An ONVIF recorder takes its channels and paths from the device (ADR-023).
  const onvifRecorder = f.recorder && f.connection === 'onvif';
  // Paths only matter (and are only sent) for direct RTSP.
  if (f.connection === 'rtsp' || (f.recorder && !onvifRecorder)) {
    full.rtsp_main_path = f.rtsp_main_path;
    full.rtsp_sub_path = f.rtsp_sub_path;
  }
  if (f.recorder) {
    // Channels are RTSP; a recorder is always on (channels are enabled one by one).
    full.connection = onvifRecorder ? 'onvif' : 'rtsp';
    // On an ONVIF recorder the count only marks a recorder: the device says how many.
    full.channel_count = onvifRecorder ? (d?.channel_count ?? 1) : Number(f.channel_count);
    delete full.enabled;
  }
  if (f.recorder || f.connection === 'onvif') full.onvif_port = Number(f.onvif_port || 0);
  if (!d) {
    if (f.password) full.password = f.password;
    if (full.onvif_port === 0) delete full.onvif_port;
    return full;
  }
  const patch = diff(full, d);
  if (mode === 'set' && f.password) patch.password = f.password;
  if (mode === 'remove') patch.password = '';
  return patch;
}

/** The fields of `full` that differ from the stored device. */
function diff(full: DeviceBody, d: Device): DeviceBody {
  const current: DeviceBody = {
    site_id: d.site.id,
    name: d.name,
    kind: d.kind,
    vendor: d.vendor,
    model: d.model,
    host: d.host,
    http_port: d.http_port,
    rtsp_port: d.rtsp_port,
    connection: d.connection,
    rtsp_main_path: d.rtsp_main_path,
    rtsp_sub_path: d.rtsp_sub_path,
    username: d.username,
    enabled: d.enabled,
    notes: d.notes,
    onvif_port: d.onvif_port ?? 0,
    ...(isRecorder(d) ? { channel_count: d.channel_count ?? 0 } : {}),
  };
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(full)) {
    const cur = current[k as keyof DeviceBody];
    const same = typeof v === 'string' && typeof cur === 'string' ? v.trim() === cur : v === cur;
    if (!same) patch[k] = v;
  }
  return patch;
}

export function DeviceDrawer({ device, sites, canManage, onClose, onOpen }: Props) {
  const { request, can } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState(() => initial(device, sites));
  const [mode, setMode] = useState<PasswordMode>(device?.has_password ? 'keep' : 'set');
  const [problem, setProblem] = useState<Problem>(noProblem);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmShrink, setConfirmShrink] = useState(false);
  const readOnly = !canManage;
  const channel = device && isChannel(device) ? device : null;
  // The device as stored now: a new one is identified in the background, so
  // poll it briefly until its identity arrives.
  const liveQuery = useQuery({
    queryKey: deviceKey(device?.id ?? ''),
    queryFn: () => getDevice(request, device?.id ?? ''),
    enabled: !!device,
    ...(device ? { initialData: device } : {}),
    refetchInterval: (q) => (q.state.data && identifying(q.state.data) ? 2000 : false),
  });
  const live = device ? (liveQuery.data ?? device) : undefined;
  /** Fields a channel inherits from its recorder are read-only on the channel. */
  const locked = readOnly || channel !== null;

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
  };

  /** Applies a template/channel choice to the path fields. */
  const choosePreset = (preset: RtspPreset, ch: string, recorder = form.recorder) => {
    const paths = recorder ? presetTemplates(preset) : presetPaths(preset, Number(ch));
    setForm((f) => ({
      ...f,
      preset,
      channel: ch,
      recorder,
      ...(paths ? { rtsp_main_path: paths.main, rtsp_sub_path: paths.sub } : {}),
    }));
  };

  /** On a new device, DVR/NVR defaults to a recorder with channels. */
  const chooseKind = (kind: DeviceKind) => {
    set('kind', kind);
    if (device) return;
    const recorder = recorderKinds.includes(kind);
    if (recorder !== form.recorder) choosePreset(form.preset, form.channel, recorder);
  };

  const openRecorder = useMutation({
    mutationFn: () => getDevice(request, channel?.parent_id ?? ''),
    onSuccess: onOpen,
    onError: (err) => {
      setProblem(toProblem(err));
    },
  });

  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: devicesKey }),
      qc.invalidateQueries({ queryKey: sitesKey }),
    ]);

  const body = toBody(form, device, device ? mode : 'set');
  const dirty = !device || Object.keys(body).length > 0;

  const save = useMutation({
    mutationFn: () =>
      device ? updateDevice(request, device.id, body) : createDevice(request, body),
    onSuccess: async (d) => {
      setConfirmShrink(false);
      await invalidate();
      if (!device && isRecorder(d)) {
        // Straight to the channels: test them and disable the unused ones.
        notify(`${d.name} cadastrado com ${String(d.channel_count)} canais.`);
        onOpen(d);
        return;
      }
      if (!device && d.connection !== 'rtmp') {
        // Stay on the new device: its identity is read in the background.
        notify(`Dispositivo ${d.name} cadastrado. Identificando o aparelho…`);
        onOpen(d);
        return;
      }
      notify(device ? 'Dispositivo atualizado.' : `Dispositivo ${d.name} cadastrado.`);
      onClose();
    },
    onError: (err) => {
      setConfirmShrink(false);
      setProblem(toProblem(err));
    },
  });
  // Fewer channels deletes the extra ones: confirm first.
  const shrinking =
    device?.channel_count != null &&
    body.channel_count !== undefined &&
    body.channel_count < device.channel_count;

  const remove = useMutation({
    mutationFn: () => deleteDevice(request, device?.id ?? ''),
    onSuccess: async () => {
      await invalidate();
      notify('Dispositivo excluído.');
      onClose();
    },
    onError: (err) => {
      setConfirmDelete(false);
      setProblem(toProblem(err));
    },
  });

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    setProblem(noProblem);
    if (shrinking) setConfirmShrink(true);
    else save.mutate();
  };

  const text = (
    key: keyof FormState,
    label: string,
    extra: { hint?: string; type?: string; mono?: boolean } = {},
  ) => (
    <Field label={label} error={problem.fields[key]} {...(extra.hint ? { hint: extra.hint } : {})}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          type={extra.type ?? 'text'}
          readOnly={key === 'name' || key === 'notes' || key === 'password' ? readOnly : locked}
          value={String(form[key])}
          onChange={(e) => {
            set(key, e.target.value as never);
            // A hand-edited path no longer follows the template.
            if (key === 'rtsp_main_path' || key === 'rtsp_sub_path') set('preset', 'custom');
          }}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={`${inputClass} ${extra.mono ? 'font-mono' : ''}`}
          {...(key === 'name' ? { 'data-autofocus': true } : {})}
        />
      )}
    </Field>
  );

  return (
    <Drawer
      title={device ? device.name : 'Novo dispositivo'}
      {...(device ? { description: `${kindLabels[device.kind]} · ${device.site.name}` } : {})}
      onClose={onClose}
      footer={
        <>
          {device && canManage && !channel && (
            <Button
              className="mr-auto text-crit!"
              onClick={() => {
                setConfirmDelete(true);
              }}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>{readOnly ? 'Fechar' : 'Cancelar'}</Button>
          {canManage && (
            <Button
              variant="primary"
              type="submit"
              form="device-form"
              disabled={!dirty || save.isPending}
            >
              {save.isPending ? 'Salvando…' : device ? 'Salvar alterações' : 'Cadastrar'}
            </Button>
          )}
        </>
      }
    >
      {channel && (
        <div className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-line-strong bg-canvas px-3 py-2.5 text-[13px] text-fg-muted">
          <span className="mr-auto">
            Canal {channel.channel} de um gravador. Endereço, portas, credenciais e caminhos vêm do
            gravador.
          </span>
          <Button
            variant="ghost"
            className="h-7"
            disabled={openRecorder.isPending}
            onClick={() => {
              openRecorder.mutate();
            }}
          >
            Abrir gravador
          </Button>
        </div>
      )}
      {device && isRecorder(device) && (
        <div className="mb-6 border-b border-line pb-6">
          <ChannelsPanel
            recorder={device}
            canManage={canManage}
            onOpenChannel={onOpen}
            onUseCount={(n) => {
              set('channel_count', String(n));
            }}
          />
        </div>
      )}
      {device && !isRecorder(device) && (
        <div className="mb-6 border-b border-line pb-6">
          <DeviceConnection device={live ?? device} canManage={canManage} />
        </div>
      )}
      {device && !isRecorder(device) && can('recordings.view') && (
        <div className="mb-6 border-b border-line pb-6">
          <RecordingSection device={device} canManage={can('recordings.manage')} />
        </div>
      )}
      <form id="device-form" onSubmit={submit} noValidate className="space-y-6">
        <FormProblem message={problem.form} />
        <Section title="Identificação">
          {text('name', 'Nome')}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo" error={problem.fields.kind}>
              {({ id, describedBy }) => (
                <select
                  id={id}
                  disabled={locked}
                  value={form.kind}
                  onChange={(e) => {
                    chooseKind(e.target.value as DeviceKind);
                  }}
                  aria-describedby={describedBy}
                  className={inputClass}
                >
                  {Object.entries(kindLabels)
                    .filter(
                      ([k]) =>
                        !(device && form.recorder) || recorderKinds.includes(k as DeviceKind),
                    )
                    .map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                </select>
              )}
            </Field>
            <Field label="Conta (local monitorado)" error={problem.fields.site_id}>
              {({ id, describedBy, invalid }) => (
                <select
                  id={id}
                  disabled={locked}
                  value={form.site_id}
                  onChange={(e) => {
                    set('site_id', e.target.value);
                  }}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  className={inputClass}
                >
                  <option value="">Selecione…</option>
                  {byClient(sites).map((g) => (
                    <optgroup key={g.client} label={g.client}>
                      {g.sites.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {text('vendor', 'Fabricante')}
            {text('model', 'Modelo')}
          </div>
          {canManage &&
            !channel &&
            live &&
            (live.detected_vendor || live.detected_model) &&
            (live.detected_vendor !== form.vendor.trim() ||
              live.detected_model !== form.model.trim()) && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-line-strong bg-canvas px-3 py-2 text-xs text-fg-muted">
                <span className="mr-auto">
                  Detectado no aparelho:{' '}
                  <span className="font-medium text-fg">
                    {[live.detected_vendor, live.detected_model].filter(Boolean).join(' ')}
                  </span>
                </span>
                <Button
                  variant="ghost"
                  className="h-7"
                  onClick={() => {
                    setForm((f) => ({
                      ...f,
                      vendor: live.detected_vendor || f.vendor,
                      model: live.detected_model || f.model,
                    }));
                  }}
                >
                  Usar dados detectados
                </Button>
              </div>
            )}
          {!device && recorderKinds.includes(form.kind) && (
            <label className="flex items-start gap-2.5 text-[13px] text-fg">
              <input
                type="checkbox"
                checked={form.recorder}
                onChange={(e) => {
                  choosePreset(form.preset, form.channel, e.target.checked);
                }}
                className="mt-0.5 size-4 accent-[var(--color-accent-strong)]"
              />
              <span>
                Cadastrar os canais do gravador
                <span className="block text-xs text-fg-subtle">
                  Endereço, portas e senha uma única vez; cada canal vira uma câmera. Desmarque para
                  cadastrar só um canal.
                </span>
              </span>
            </label>
          )}
        </Section>

        <Section title="Rede e conexão">
          {form.recorder ? (
            <>
              <ConnectionChoice
                value={form.connection === 'onvif' ? 'onvif' : 'rtsp'}
                options={recorderConnectionOptions}
                disabled={locked || !!device}
                onChange={(c) => {
                  set('connection', c);
                }}
              />
              <p className="text-xs text-fg-muted">
                {form.connection === 'onvif'
                  ? 'Ao cadastrar, o gravador informa por ONVIF os seus canais (com a numeração real) e os endereços de vídeo; o vídeo de cada canal vem por RTSP. Câmeras ligadas depois entram com "Redescobrir canais".'
                  : 'Os canais conectam por RTSP direto com o modelo de URL. A porta ONVIF, quando o gravador a oferece, serve para detectar a quantidade de canais e receber eventos.'}
                {device && ' O modo de conexão do gravador não muda depois do cadastro.'}
              </p>
              {problem.fields.connection && (
                <p role="alert" className="text-xs text-crit">
                  {problem.fields.connection}
                </p>
              )}
            </>
          ) : (
            <ConnectionChoice
              value={form.connection}
              disabled={locked}
              onChange={(c) => {
                set('connection', c);
              }}
            />
          )}
          {(form.recorder || form.connection !== 'rtmp') &&
            text('host', 'Endereço', {
              hint: 'IP ou nome de host, sem protocolo nem porta.',
              mono: true,
            })}
          {(form.recorder || form.connection === 'onvif') && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                {text('http_port', 'Porta web', { type: 'number', mono: true })}
                {!locked && (
                  <div className="mt-1 flex gap-1">
                    {['80', '8080'].map((port) => (
                      <button
                        key={port}
                        type="button"
                        aria-pressed={form.http_port === port}
                        onClick={() => {
                          set('http_port', port);
                        }}
                        className={`rounded px-1.5 py-0.5 font-mono text-2xs ${
                          form.http_port === port
                            ? 'bg-accent-soft text-accent'
                            : 'text-fg-subtle hover:bg-surface-hover hover:text-fg'
                        }`}
                      >
                        {port}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {text('onvif_port', 'Porta ONVIF', {
                type: 'number',
                mono: true,
                hint: 'Vazio: a mesma da web.',
              })}
              {text('rtsp_port', 'Porta RTSP', { type: 'number', mono: true })}
            </div>
          )}
          {form.recorder && form.connection === 'onvif' ? null : form.recorder ? (
            <>
              <Field label="Modelo de URL">
                {({ id, describedBy }) => (
                  <select
                    id={id}
                    disabled={locked}
                    value={form.preset}
                    onChange={(e) => {
                      choosePreset(e.target.value as RtspPreset, form.channel);
                    }}
                    aria-describedby={describedBy}
                    className={inputClass}
                  >
                    {Object.entries(rtspPresetLabels).map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              {text('rtsp_main_path', 'Caminho do stream principal', {
                mono: true,
                hint: `${CHANNEL} é trocado pelo número de cada canal. Canal 2: ${renderTemplate(form.rtsp_main_path, 2) || '—'}`,
              })}
              {text('rtsp_sub_path', 'Caminho do stream secundário', {
                mono: true,
                hint: 'Opcional; usado nos mosaicos por consumir menos banda.',
              })}
              <ChannelCount
                value={form.channel_count}
                current={device?.channel_count ?? null}
                disabled={locked}
                error={problem.fields.channel_count}
                onChange={(v) => {
                  set('channel_count', v);
                }}
              />
            </>
          ) : form.connection === 'rtmp' ? (
            <PushKeyPanel device={device} canManage={canManage} />
          ) : form.connection === 'onvif' ? null : (
            <>
              <div className="grid grid-cols-[1fr_5.5rem_6.5rem] gap-3">
                <Field label="Modelo de URL">
                  {({ id, describedBy }) => (
                    <select
                      id={id}
                      disabled={locked}
                      value={form.preset}
                      onChange={(e) => {
                        choosePreset(e.target.value as RtspPreset, form.channel);
                      }}
                      aria-describedby={describedBy}
                      className={inputClass}
                    >
                      {Object.entries(rtspPresetLabels).map(([k, l]) => (
                        <option key={k} value={k}>
                          {l}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                <Field label="Canal">
                  {({ id, describedBy }) => (
                    <input
                      id={id}
                      type="number"
                      min={1}
                      max={256}
                      readOnly={locked}
                      disabled={form.preset === 'custom'}
                      value={form.channel}
                      onChange={(e) => {
                        choosePreset(form.preset, e.target.value);
                      }}
                      aria-describedby={describedBy}
                      className={`${inputClass} font-mono`}
                    />
                  )}
                </Field>
                {text('rtsp_port', 'Porta RTSP', { type: 'number', mono: true })}
              </div>
              {text('rtsp_main_path', 'Caminho do stream principal', { mono: true })}
              {text('rtsp_sub_path', 'Caminho do stream secundário', {
                mono: true,
                hint: channel
                  ? 'Vem do modelo de URL do gravador.'
                  : 'Opcional; usado nos mosaicos por consumir menos banda.',
              })}
            </>
          )}
        </Section>

        <Section title="Credenciais">
          {text('username', 'Usuário', { mono: true })}
          {channel ? (
            <p className="text-xs text-fg-subtle">
              {channel.has_password ? 'Senha definida no gravador.' : 'Sem senha no gravador.'}
            </p>
          ) : device && mode !== 'set' ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 rounded-md border border-line-strong bg-canvas px-3 py-2.5">
                <span className="flex items-center gap-2 text-[13px] text-fg-muted">
                  <KeyRound aria-hidden="true" className="size-4" strokeWidth={1.75} />
                  {mode === 'keep'
                    ? 'Senha salva (criptografada)'
                    : 'A senha será removida ao salvar'}
                </span>
                {canManage && (
                  <span className="flex gap-1">
                    {mode === 'keep' ? (
                      <>
                        <Button
                          variant="ghost"
                          className="h-7"
                          onClick={() => {
                            setMode('set');
                          }}
                        >
                          Alterar
                        </Button>
                        <Button
                          variant="ghost"
                          className="h-7 text-crit!"
                          onClick={() => {
                            setMode('remove');
                          }}
                        >
                          Remover
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="ghost"
                        className="h-7"
                        onClick={() => {
                          setMode('keep');
                        }}
                      >
                        Desfazer
                      </Button>
                    )}
                  </span>
                )}
              </div>
              {mode === 'keep' && can('devices.password') && <SavedPassword deviceId={device.id} />}
            </div>
          ) : (
            canManage && (
              <div>
                <DevicePasswordInput
                  label={device?.has_password ? 'Nova senha' : 'Senha'}
                  value={form.password}
                  error={problem.fields.password}
                  generated={!device}
                  onChange={(v) => {
                    set('password', v);
                  }}
                />
                {device?.has_password && (
                  <Button
                    variant="ghost"
                    className="mt-1 h-7"
                    onClick={() => {
                      set('password', '');
                      setMode('keep');
                    }}
                  >
                    Manter a senha atual
                  </Button>
                )}
              </div>
            )
          )}
        </Section>

        <Section title="Operação">
          <label
            className={`flex items-center gap-2.5 text-[13px] text-fg ${form.recorder ? 'hidden' : ''}`}
          >
            <input
              type="checkbox"
              disabled={readOnly}
              checked={form.enabled}
              onChange={(e) => {
                set('enabled', e.target.checked);
              }}
              className="size-4 accent-[var(--color-accent-strong)]"
            />
            Habilitado
            <span className="text-xs text-fg-subtle">
              — dispositivos desabilitados não são monitorados
            </span>
          </label>
          <Field label="Observações" error={problem.fields.notes}>
            {({ id, describedBy }) => (
              <textarea
                id={id}
                readOnly={readOnly}
                rows={3}
                value={form.notes}
                onChange={(e) => {
                  set('notes', e.target.value);
                }}
                aria-describedby={describedBy}
                className={`${inputClass} h-auto py-2`}
              />
            )}
          </Field>
        </Section>
      </form>

      {confirmDelete && device && (
        <ConfirmDialog
          title={`Excluir ${device.name}?`}
          confirmLabel="Excluir dispositivo"
          busy={remove.isPending}
          onCancel={() => {
            setConfirmDelete(false);
          }}
          onConfirm={() => {
            remove.mutate();
          }}
        >
          {isRecorder(device)
            ? `O gravador e os seus ${String(device.channel_count)} canais serão removidos, com as credenciais armazenadas. Gravações e eventos já registrados permanecem até expirar. Esta ação não pode ser desfeita.`
            : 'O dispositivo e as credenciais armazenadas serão removidos. Esta ação não pode ser desfeita.'}
        </ConfirmDialog>
      )}
      {confirmShrink && device?.channel_count != null && (
        <ConfirmDialog
          title={`Reduzir para ${form.channel_count} canais?`}
          confirmLabel="Reduzir e salvar"
          busy={save.isPending}
          onCancel={() => {
            setConfirmShrink(false);
          }}
          onConfirm={() => {
            save.mutate();
          }}
        >
          {`Os canais ${String(Number(form.channel_count) + 1)} a ${String(device.channel_count)} serão excluídos, com a configuração de gravação deles. Para manter um canal sem uso, desative-o em vez de reduzir.`}
        </ConfirmDialog>
      )}
    </Drawer>
  );
}

const connectionOptions: { value: Connection; title: string; detail: string }[] = [
  {
    value: 'rtsp',
    title: 'RTSP direto',
    detail: 'Só a porta RTSP (554), aberta no local.',
  },
  { value: 'onvif', title: 'ONVIF', detail: 'Descobre os perfis pela porta HTTP.' },
  {
    value: 'rtmp',
    title: 'RTMP reverso',
    detail: 'O aparelho envia ao servidor; sem portas abertas.',
  },
];

const recorderConnectionOptions: { value: Connection; title: string; detail: string }[] = [
  {
    value: 'rtsp',
    title: 'RTSP direto',
    detail: 'Canais pelo modelo de URL; só a porta RTSP (554).',
  },
  {
    value: 'onvif',
    title: 'ONVIF',
    detail: 'O gravador informa os canais e endereços (porta web/ONVIF).',
  },
];

function ConnectionChoice({
  value,
  options = connectionOptions,
  disabled,
  onChange,
}: {
  value: Connection;
  options?: { value: Connection; title: string; detail: string }[];
  disabled: boolean;
  onChange: (c: Connection) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Modo de conexão"
      className={`grid gap-2 ${options.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}
    >
      {options.map((o) => (
        <label
          key={o.value}
          className={`flex flex-col gap-0.5 rounded-md border px-3 py-2 transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent ${
            value === o.value
              ? 'border-accent bg-accent-soft'
              : 'border-line-strong bg-canvas hover:border-fg-subtle'
          } ${disabled ? '' : 'cursor-pointer'}`}
        >
          <span className="flex items-center gap-2 text-[13px] font-medium text-fg">
            <input
              type="radio"
              name="connection"
              value={o.value}
              checked={value === o.value}
              disabled={disabled}
              onChange={() => {
                onChange(o.value);
              }}
              className="size-3.5 accent-[var(--color-accent-strong)]"
            />
            {o.title}
          </span>
          <span className="text-xs text-fg-subtle">{o.detail}</span>
        </label>
      ))}
    </div>
  );
}

function ChannelCount({
  value,
  current,
  disabled,
  error,
  onChange,
}: {
  value: string;
  current: number | null;
  disabled: boolean;
  error: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <Field
      label="Quantidade de canais"
      error={error}
      hint={
        current !== null && Number(value) !== current
          ? `Hoje: ${String(current)}. Ao salvar, canais serão ${Number(value) > current ? 'criados' : 'excluídos'}.`
          : 'Depois de cadastrar, teste os canais e desative os que não estão em uso.'
      }
    >
      {({ id, describedBy, invalid }) => (
        <div className="flex items-center gap-2">
          <div role="group" aria-label="Quantidades comuns" className="flex gap-1">
            {channelPresets.map((n) => (
              <button
                key={n}
                type="button"
                disabled={disabled}
                aria-pressed={value === String(n)}
                onClick={() => {
                  onChange(String(n));
                }}
                className={`h-8 min-w-10 rounded-md border px-2 font-mono text-[13px] ${
                  value === String(n)
                    ? 'border-accent bg-accent-soft text-fg'
                    : 'border-line-strong bg-canvas text-fg-muted hover:border-fg-subtle'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <input
            id={id}
            type="number"
            min={1}
            max={64}
            readOnly={disabled}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            className={`${inputClass} w-20! font-mono`}
          />
        </div>
      )}
    </Field>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-3">
      <legend className="mb-2 text-2xs font-medium tracking-wide text-fg-subtle uppercase">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}
